import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Avatar,
  Grid,
  Container,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  PhotoCamera,
  CheckCircle,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';
import { OTP_TYPES } from 'figshelf-modules';
import SocialButtons from '../components/SocialButtons';
import AuthPageLayout from '../components/AuthPageLayout';
import { CountrySelect, CitySelect } from '../components/CountryCitySelect';
import { useAuthModal } from '../contexts/AuthModalContext';
import OTPEditor from '../components/OTPEditor';

const Register = ({ embedded = false }) => {
  const navigate = useNavigate();
  const auth = useContext(CONTEXTS.AuthContext);
  const api = useContext(CONTEXTS.APIContext);
  const authController = new CONTROLLERS.AuthController(api);
  const authModal = useAuthModal();

  const errorFieldSx = {
    '& .MuiFormHelperText-root': { ml: 0 },
    '& .MuiFormHelperText-root.Mui-error': { fontWeight: 500 },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'error.main',
      borderWidth: 2,
    },
  };

  // Multi-step form state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Account Info', 'Email Verification', 'Profile Setup'];

  // Step 1: Account Information
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Step 2: Email Verification
  const [otp, setOtp] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);

  // Step 3: Profile Setup
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState(null);
  const [city, setCity] = useState('');
  const [pronoun, setPronoun] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Form validation and loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [otpError, setOtpError] = useState('');

  // OTP countdown timer
  useEffect(() => {
    let interval;
    if (otpExpiresIn > 0) {
      interval = setInterval(() => {
        setOtpExpiresIn((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpExpiresIn]);

  const validateStep1 = () => {
    let isValid = true;
    setUsernameError('');
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setError('');

    if (!username.trim()) {
      setUsernameError('Username is required');
      isValid = false;
    }

    if (!email.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Invalid email format');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      isValid = false;
    } else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password)
    ) {
      setPasswordError(
        'Password must contain uppercase, lowercase, number, and special character'
      );
      isValid = false;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      isValid = false;
    }

    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      isValid = false;
    }

    return isValid;
  };

  const handleStep1Submit = async () => {
    if (!validateStep1()) return;

    setIsLoading(true);
    setError('');

    try {
      // Send email verification OTP
      const verifyRes = await authController.sendOTP(email, OTP_TYPES.SIGNUP);
      
      if (verifyRes.statusCode === 201) {
        setOtpExpiresIn(300); // 5 minutes
        setCanResendOtp(false);
        setActiveStep(1);
      }
    } catch (err) {
      console.error('Email verification error:', err);
      if (err.response?.data?.message?.includes('already exists')) {
        setEmailError('Email already registered');
      } else {
        setError(err.message || 'Failed to send verification email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerification = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setOtpError('');

    try {
      // Verify OTP
      const confirmRes = await authController.verifyOTP(email, otp, OTP_TYPES.SIGNUP);

      if (confirmRes.statusCode === 200 || confirmRes.statusCode === 201) {
        setActiveStep(2);
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setOtpError('Invalid or expired code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setOtpError('');

    try {
      await authController.sendOTP(email, OTP_TYPES.SIGNUP);
      setOtpExpiresIn(300);
      setCanResendOtp(false);
      setOtp('');
    } catch (err) {
      setOtpError('Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleFinalSubmit = async () => {
    setIsLoading(true);
    setError('');

    try {
      const authCredsJson = localStorage.getItem('auth_credentials');
      const authCreds = authCredsJson ? JSON.parse(authCredsJson) : null;
      const verificationToken = authCreds?.token || null;

      const userData = {
        username,
        email,
        password,
        verificationToken,
      };
      
      if (displayName) userData.displayName = displayName;
      if (bio) userData.bio = bio;
      if (country) userData.country = country;
      if (city) userData.city = city;
      if (pronoun) userData.pronoun = pronoun;

      const res = await authController.finalSignUp(userData, avatarFile);

      // Sign in with the returned tokens
      await auth.signIn(res.data.accessToken, res.data.refreshToken);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <Box>
      <TextField
        label="Username"
        variant="outlined"
        fullWidth
        value={username}
        onChange={(e) => {
          setUsername(e.target.value);
          setUsernameError('');
        }}
        error={!!usernameError}
        helperText={usernameError}
        disabled={isLoading}
        sx={{ mb: 2, ...errorFieldSx }}
      />

      <TextField
        label="Email"
        variant="outlined"
        fullWidth
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setEmailError('');
        }}
        error={!!emailError}
        helperText={emailError}
        disabled={isLoading}
        sx={{ mb: 2, ...errorFieldSx }}
      />

      <TextField
        label="Password"
        variant="outlined"
        fullWidth
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          setPasswordError('');
        }}
        error={!!passwordError}
        helperText={passwordError}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, ...errorFieldSx }}
      />

      <TextField
        label="Confirm Password"
        variant="outlined"
        fullWidth
        type={showConfirmPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          setConfirmPasswordError('');
        }}
        error={!!confirmPasswordError}
        helperText={confirmPasswordError}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, ...errorFieldSx }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I agree to the{' '}
            <Link href="/terms" target="_blank">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href="/privacy" target="_blank">
              Privacy Policy
            </Link>
          </Typography>
        }
        sx={{ mb: 3 }}
      />

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleStep1Submit}
        disabled={isLoading}
        sx={{
          py: 1.5,
          background: 'primary.main',
          '&:hover': {
            background: 'primary.dark',
          },
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
      </Button>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircle sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Verify Your Email
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We've sent a 6-digit code to <strong>{email}</strong>
        </Typography>
      </Box>

      <OTPEditor
        length={6}
        value={otp}
        onChange={(next) => {
          setOtp(next);
          setOtpError('');
        }}
        onComplete={(next) => {
          setOtp(next);
          setOtpError('');
        }}
        disabled={isLoading}
        error={otpError}
      />

      {otpExpiresIn > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          Code expires in {Math.floor(otpExpiresIn / 60)}:
          {(otpExpiresIn % 60).toString().padStart(2, '0')}
        </Typography>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleOtpVerification}
        disabled={isLoading || otp.length !== 6}
        sx={{
          py: 1.5,
          mb: 2,
          background: (theme) => theme.palette.mode === 'dark' ? 'primary.main' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: (theme) => theme.palette.mode === 'dark' ? 'primary.dark' : 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
          },
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
      </Button>

      <Button
        variant="text"
        fullWidth
        onClick={handleResendOtp}
        disabled={!canResendOtp || isLoading}
      >
        Resend Code
      </Button>
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Complete Your Profile
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={avatarPreview}
            sx={{ width: 100, height: 100, bgcolor: 'primary.main' }}
          >
            {username[0]?.toUpperCase()}
          </Avatar>
          <IconButton
            component="label"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <PhotoCamera />
            <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
          </IconButton>
        </Box>
      </Box>

      <TextField
        label="Display Name"
        variant="outlined"
        fullWidth
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        disabled={isLoading}
        sx={{ mb: 2, ...errorFieldSx }}
      />

      <TextField
        label="Bio"
        variant="outlined"
        fullWidth
        multiline
        rows={3}
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        disabled={isLoading}
        sx={{ mb: 2, ...errorFieldSx }}
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <CountrySelect
            value={country}
            onChange={(nextCountry) => {
              setCountry(nextCountry);
              setCity('');
            }}
            onSelect={(option) => {
              setCountryCode(option?.code || null);
            }}
            disabled={isLoading}
            textFieldProps={{
              sx: { width: '100%', ...errorFieldSx },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CitySelect
            label="City (Optional)"
            value={typeof city === 'string' ? city : city?.name || ''}
            onChange={(nextCity) => {
              // CitySelect provides either a string or an object { name, admin1 }
              if (nextCity && typeof nextCity === 'object') {
                setCity(nextCity.name || '');
              } else {
                setCity(nextCity || '');
              }
            }}
            countryCode={countryCode}
            disabled={isLoading}
            textFieldProps={{
              sx: { width: '100%', ...errorFieldSx },
            }}
          />
        </Grid>
      </Grid>

      <TextField
        label="Pronoun (Optional)"
        variant="outlined"
        fullWidth
        select
        value={pronoun}
        onChange={(e) => setPronoun(e.target.value)}
        disabled={isLoading}
        sx={{ mb: 3 }}
      >
        <MenuItem value="He/Him">He/Him</MenuItem>
        <MenuItem value="She/Her">She/Her</MenuItem>
        <MenuItem value="They/Them">They/Them</MenuItem>
        <MenuItem value="Other">Other</MenuItem>
        <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
      </TextField>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleFinalSubmit}
        disabled={isLoading}
        sx={{
          py: 1.5,
          mb: 2,
          background: (theme) => theme.palette.mode === 'dark' ? 'primary.main' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: (theme) => theme.palette.mode === 'dark' ? 'primary.dark' : 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
          },
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Complete Registration'}
      </Button>

      <Button variant="text" fullWidth onClick={() => handleFinalSubmit()}>
        Skip for Now
      </Button>
    </Box>
  );

  const content = (
    <>
          {/* Back Button */}
          <IconButton
            onClick={() => {
              if (activeStep > 0) {
                setActiveStep(activeStep - 1);
                return;
              }
              if (embedded) {
                authModal.openLogin();
                return;
              }
              navigate('/login');
            }}
            sx={
              embedded
                ? { color: 'primary.main', mb: 1 }
                : {
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    color: 'primary.main',
                  }
            }
          >
            <ArrowBack />
          </IconButton>

          {/* Logo */}
          {!embedded && (
            <Box sx={{ textAlign: 'center', mb: 2, mt: 1 }}>
              <img
                src="/assets/logos/logo_1.png"
                alt="FigShelf Logo"
                style={{ height: 56 }}
              />
            </Box>
          )}

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 2.5 }}>
            <Typography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              sx={{
                color: 'primary.main',
              }}
            >
              Create Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Join FigShelf community today
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 2.5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
          {activeStep === 2 && renderStep3()}

          {/* Social Login & Sign In Link */}
          {activeStep === 0 && (
            <>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              {/* Social Login Buttons */}
              <Box sx={{ mb: 3 }}>
                <SocialButtons />
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{' '}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      if (embedded) {
                        authModal.openLogin();
                        return;
                      }
                      navigate('/login');
                    }}
                    sx={{
                      fontWeight: 'bold',
                      textDecoration: 'none',
                      color: 'primary.main',
                    }}
                  >
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </>
          )}
    </>
  );

  if (embedded) {
    return <Box sx={{ width: '100%' }}>{content}</Box>;
  }

  return (
    <AuthPageLayout paperMaxWidth={560}>
      {content}
    </AuthPageLayout>
  );
};

export default Register;
